import { convertPriceTotalWithRounding } from '@apps/card/hooks/convertPriceTotalWithRounding';
import { calculateStationeryPricing } from '@shared/utils/print-price/utils/calculatePrices';
import { calculateDiscount } from '@shared/utils/print-price/utils/calculateDiscounts';
import type { StationeryDiscountInfo } from '@shared/utils/print-price/types';
import type { CardPdpSeoDataQuery } from './ProductDetailsPage.graphql.generated';
import type { PdpSeoOffer } from './ProductDetailsPage.types';
import { PDP_SEO_OFFER_PRODUCT_TYPE, PDP_SEO_OFFER_QUANTITY } from './constants';

type PdpSeoProduct = CardPdpSeoDataQuery['stationeryTemplatesByThemeId'][number]['products'][number];

type GetPdpSeoOfferOptions = Readonly<{
  productType?: string;
  quantity?: number;
  /**
   * The promotion the page applies to this product, if any (the PDP's format picker resolves one
   * per vendor through `useGetDiscountByVendor`). The advertised price must be the price the page
   * shows, so a sale lowers the offer the same way it lowers the picker's row.
   */
  getDiscount?: (product: PdpSeoProduct) => StationeryDiscountInfo | undefined;
}>;

/**
 * The price a product page advertises to search engines: the paper product's base price at the
 * PDP's default quantity, with no options or currency conversion applied, and with the page's own
 * promotion applied when one is active. Mirrors `PrintPriceCalculator.calculatePriceWithoutOptions`
 * (`getProductBasePrice` with `excludeOptions`), including the rounding that keeps the total
 * divisible by the quantity, so the number here is the number the format picker shows.
 *
 * Returns null when the template has no such product or its pricing has no tier at the quantity,
 * so callers omit the offer rather than publish a wrong number.
 */
export const getPdpSeoOffer = (products: ReadonlyArray<PdpSeoProduct> | undefined, options: GetPdpSeoOfferOptions = {}): PdpSeoOffer | null => {
  const { productType = PDP_SEO_OFFER_PRODUCT_TYPE, quantity = PDP_SEO_OFFER_QUANTITY, getDiscount } = options;
  const product = products?.find(p => p.productType === productType);
  if (!product || quantity <= 0) return null;

  let basePrice: number;
  try {
    basePrice = calculateStationeryPricing(product.pricing, quantity);
  } catch {
    // `calculateStationeryPricing` throws when no tier covers the quantity.
    return null;
  }

  const discount = getDiscount?.(product);
  const discounted = discount ? calculateDiscount(discount, basePrice) : { totalPriceInMinorUnits: basePrice, hasDiscount: false as const };
  // Same order as the calculator: discount the raw total, then round it to a whole number of
  // cards, then floor the per-card price. The list price rounds the raw total the same way.
  const totalPriceInMinorUnits = convertPriceTotalWithRounding(discounted.totalPriceInMinorUnits, quantity).resultWithFloor;
  const listTotalInMinorUnits = convertPriceTotalWithRounding(basePrice, quantity).resultWithFloor;
  return {
    productId: product.id,
    productType: product.productType,
    quantity,
    unitPriceInMinorUnits: Math.floor(totalPriceInMinorUnits / quantity),
    totalPriceInMinorUnits,
    listUnitPriceInMinorUnits: Math.floor(listTotalInMinorUnits / quantity),
    currency: product.priceCurrency,
  };
};
